/**
 * Home "user health" math — ported 1:1 from the legacy task-manager app
 * (`computeUserAssignedHealth` / `makeUserHealthBar` in app-core.js, plus
 * `managementHealthPaceEmoji` from app-shell.js / app-gamification.js).
 *
 * Health = completed assigned hours vs the monthly working capacity
 * ({@link TOTAL_MONTH_HOURS}). Any overdue assigned task forces the bar to a red
 * 15% cap. Everything here is derived client-side from per-user hour totals plus
 * the current date — no extra API surface.
 */
import {
  TOTAL_MONTH_HOURS,
  workdayMonthProgress,
  expectedMonthHoursSoFar,
  statusForPct,
  type MetricStatus,
} from '@schwrz/shared';

export { round1 } from '@schwrz/shared';

/** Tier classification used for both the fill colour and the pulse-when-red flag. */
export type HealthTier = Exclude<MetricStatus, 'idle' | 'na'>;

export interface UserHealthInput {
  /** All assigned hours (open + done). */
  assignedHours: number;
  /** Completed assigned hours (done). */
  completedHours: number;
  /** Whether the user has any overdue (past-due, still-open) assigned task. */
  overdue: boolean;
}

export interface UserHealth {
  /** No assigned work and no overdue tasks → render the muted "—" state. */
  na: boolean;
  overdue: boolean;
  /** Effective fill width / aria value, 0–100 (overdue forces 15). */
  pct: number;
  /** Tier driving the fill colour; `null` while in the N/A state. */
  tier: HealthTier | null;
  /** Whether the fill should pulse (red / bad tier). */
  pulse: boolean;
  /** Pace emoji for the side column and ruler tick. */
  emoji: string;
  /** Completed assigned hours (rounded for display by the caller). */
  completedHours: number;
  /** Hours expected by now = monthly budget × share of workdays elapsed. */
  expectedSoFar: number;
  /** Share of month workdays elapsed (0–1), Mon–Fri only. */
  monthProgress: number;
}

/** Pace emoji by health %. Legacy `managementHealthPaceEmoji`. */
export function healthPaceEmoji(healthPct: number, isNaState: boolean): string {
  if (isNaState) return '📊';
  if (healthPct > 100) return '🤠';
  if (healthPct < 15) return '🤬';
  const decade = Math.min(10, Math.floor(healthPct / 10));
  const byDecade: Record<number, string> = {
    1: '😰',
    2: '😟',
    3: '😐',
    4: '😑',
    5: '🙂',
    6: '😊',
    7: '😄',
    8: '😁',
    9: '🤩',
    10: '🥳',
  };
  return byDecade[decade] ?? '😐';
}

/**
 * Today + current time for the health-bar header, e.g. «Пятница, 12 июня 13:49».
 */
export function formatTodayLabel(now: Date = new Date()): string {
  const weekdayRaw = now.toLocaleDateString('ru-RU', { weekday: 'long' });
  const weekday = weekdayRaw.charAt(0).toUpperCase() + weekdayRaw.slice(1);
  const dayMonth = now.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${weekday}, ${dayMonth} ${hours}:${minutes}`;
}

/**
 * Compute the full health-bar view-model from per-user hour totals + the current
 * date. Mirrors legacy `computeUserAssignedHealth` + the overdue/tier handling
 * in `makeUserHealthBar`.
 */
export function computeUserHealth(input: UserHealthInput, now: Date = new Date()): UserHealth {
  const assignedHours = Math.max(0, Number(input.assignedHours) || 0);
  const completedHours = Math.max(0, Number(input.completedHours) || 0);
  const overdue = Boolean(input.overdue);
  const monthProgress = workdayMonthProgress(now);
  const expectedSoFar = expectedMonthHoursSoFar(now);

  // Legacy `na` = no assigned and no completed work; overdue still renders a bar.
  const rawNa = assignedHours <= 0 && completedHours <= 0;
  const effectiveNa = rawNa && !overdue;

  const rawPct = rawNa
    ? 0
    : Math.min(100, TOTAL_MONTH_HOURS > 0 ? (completedHours / TOTAL_MONTH_HOURS) * 100 : 0);
  const computedPct = Number.isFinite(rawPct) ? rawPct : 0;
  const effectivePct = overdue ? 15 : computedPct;

  if (effectiveNa) {
    return {
      na: true,
      overdue: false,
      pct: 0,
      tier: null,
      pulse: false,
      emoji: healthPaceEmoji(0, true),
      completedHours,
      expectedSoFar,
      monthProgress,
    };
  }

  const tier: HealthTier = overdue
    ? 'bad'
    : (statusForPct(effectivePct, 'higher-better') as HealthTier);
  return {
    na: false,
    overdue,
    pct: Math.max(0, Math.min(100, effectivePct)),
    tier,
    pulse: tier === 'bad',
    emoji: healthPaceEmoji(effectivePct, false),
    completedHours,
    expectedSoFar,
    monthProgress,
  };
}
